import { useState } from "react";
import { ago, api, Empty, fmtDate, Loading, Pill, useLoad, useMe } from "./lib";

const AREA_LABEL: Record<string, string> = { marketing: "Marketing", product: "Product", both: "Both", hidden: "Hidden" };

// Figma design files for one area (Marketing graphics, Product designs). Read-only; everything opens in Figma.
export function FigmaDesigns({ area }: { area: "marketing" | "product" }) {
  const { toast } = useMe();
  const { data, error, reload } = useLoad<any>(`/api/figma?area=${area}`);
  const [folder, setFolder] = useState("");
  const [busy, setBusy] = useState(false);
  const [managing, setManaging] = useState(false);
  const [link, setLink] = useState("");
  const [newArea, setNewArea] = useState<string>(area);
  if (!data) return <Loading error={error} />;
  const integ = data.integration;
  const files = data.files.filter((f: any) => !folder || f.folder === folder);
  const call = async (fn: () => Promise<any>, ok?: string) => {
    setBusy(true);
    try {
      const r = await fn();
      if (r?.error) toast(r.error, true);
      else if (ok) toast(ok);
      reload();
      return r;
    } catch (e: any) {
      toast(e.message, true);
    } finally {
      setBusy(false);
    }
  };
  const sync = () => call(() => api("/api/figma/sync", { method: "POST" }).then((r) => (r.result === "ok" ? r : { error: r.integration?.last_error ?? "Not connected" })), "Synced with Figma");
  const add = async () => {
    const r = await call(() => api("/api/figma/files", { method: "POST", body: { url: link, area: newArea } }));
    if (r && !r.error) (setLink(""), toast(`Added ${r.name}`));
  };

  return (
    <div className="stack" style={{ gap: 16 }}>
      <section className="card row between">
        <div className="stack" style={{ gap: 2 }}>
          <strong>Figma{integ?.last_detail?.as ? ` · as ${integ.last_detail.as}` : ""}</strong>
          <span className="muted small">
            {integ?.status === "ok"
              ? `Read-only. Last synced ${ago(integ.last_success_at)}; checks every hour. Comments waiting on you go to Today.${integ.last_detail?.unreadable?.length ? ` ${integ.last_detail.unreadable.length} file${integ.last_detail.unreadable.length === 1 ? "" : "s"} couldn't be opened.` : ""}`
              : integ?.status === "error"
                ? integ.last_error
                : "Not connected yet. Needs a read-only Figma token (see docs/FIGMA-SETUP.md)."}
          </span>
        </div>
        <div className="row">
          {data.can_manage && <button className="btn sm" onClick={() => setManaging(!managing)}>{managing ? "Done" : "Add or sort files"}</button>}
          {data.can_sync && <button className="btn sm" disabled={busy} onClick={sync}>{busy ? "Syncing…" : "Sync now"}</button>}
        </div>
      </section>

      {managing && (
        <section className="card stack">
          <span className="muted small">Paste a Figma file's link (from the address bar or Share → Copy link), then pick where it shows.</span>
          <div className="row" style={{ gap: 6, flexWrap: "wrap" }}>
            <input value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://www.figma.com/design/…" aria-label="Figma file link" className="input" style={{ flex: 1, minWidth: 240, width: "auto" }} onKeyDown={(e) => e.key === "Enter" && link && add()} />
            <select className="input" style={{ width: "auto" }} value={newArea} onChange={(e) => setNewArea(e.target.value)} aria-label="Show on">
              {["marketing", "product", "both"].map((a) => <option key={a} value={a}>{AREA_LABEL[a]}</option>)}
            </select>
            <button className="btn primary sm" disabled={busy || !link.trim()} onClick={add}>{busy ? "Adding…" : "Add file"}</button>
          </div>
          {data.all_files?.map((f: any) => (
            <div key={f.key} className="row between" style={{ gap: 8 }}>
              <span className="small">{f.name ?? <span className="muted">Not synced yet ({f.key.slice(0, 8)}…)</span>}</span>
              <div className="row" style={{ gap: 6 }}>
                <div className="tabs">
                  {Object.keys(AREA_LABEL).map((a) => (
                    <button key={a} className={f.area === a ? "on" : ""} onClick={() => call(() => api(`/api/figma/files/${f.key}`, { method: "PATCH", body: { area: a } }))}>{AREA_LABEL[a]}</button>
                  ))}
                </div>
                <button className="btn ghost sm" onClick={() => call(() => api(`/api/figma/files/${f.key}`, { method: "DELETE" }))}>Remove</button>
              </div>
            </div>
          ))}
        </section>
      )}

      {data.folders.length > 1 && (
        <div className="tabs" style={{ flexWrap: "wrap" }}>
          <button className={!folder ? "on" : ""} onClick={() => setFolder("")}>All folders</button>
          {data.folders.map((p: string) => <button key={p} className={folder === p ? "on" : ""} onClick={() => setFolder(p)}>{p}</button>)}
        </div>
      )}

      {files.length ? (
        <div style={{ display: "grid", gap: 14, gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 220px), 1fr))" }}>
          {files.map((f: any) => (
            <a key={f.key} className="card" href={f.url} target="_blank" rel="noreferrer" style={{ padding: 0, overflow: "hidden", textDecoration: "none", color: "inherit" }}>
              <div style={{ aspectRatio: "16 / 10", background: "var(--panel-2, #f3f1f6)" }}>
                {f.thumbnail_url && <img src={f.thumbnail_url} alt="" loading="lazy" referrerPolicy="no-referrer" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />}
              </div>
              <div className="stack" style={{ gap: 2, padding: "10px 12px" }}>
                <strong style={{ fontSize: 14 }}>{f.name}</strong>
                <span className="muted tiny">{f.folder} · edited {fmtDate(f.last_modified.slice(0, 10))}</span>
                {f.waiting_comments > 0 && <span><Pill tone="accent">{f.waiting_comments} comment{f.waiting_comments === 1 ? "" : "s"} waiting on you</Pill></span>}
              </div>
            </a>
          ))}
        </div>
      ) : (
        <Empty>{integ?.status === "ok" ? "No files here yet. Use Add or sort files." : "Connect Figma to see designs here."}</Empty>
      )}
    </div>
  );
}
